import React, { useMemo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Visibility } from '@material-ui/icons';
import { parseISO, format } from 'date-fns';
import { isArray } from 'lodash';

import { Tabela, Modal } from '~/components';
import { t } from '~/helpers';
import api from '~/services/api';
import {
  dataInRequest,
  recordFiltering,
} from '~/store/modules/auditoriaSistemas/actions';

import ItemDetail from './ItemDetail';

export default function AuditoriaSistemas() {
  const redux = useSelector((state) => state.auditoriaSistemas);
  const modalRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const optionsActions = useMemo(
    () => [
      { label: 'A', value: t('s.geral.botaoacesso') },
      { label: 'I', value: t('s.geral.botaoincluir') },
      { label: 'U', value: t('s.geral.botaoalterar') },
      { label: 'D', value: t('s.geral.botaoexcluir') },
      { label: 'R', value: t('s.geral.botaorelatorio') },
    ],
    []
  );

  const header = useMemo(
    () => [
      {
        id: 'datahorainicioauditoriasistema',
        label: t('s.operacao.recebimento.datahorainiciodescarga'),
        format: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'initialdatetime',
          defaultValue: format(new Date(), 'yyyy-MM-dd 00:00:00'),
        },
      },
      {
        id: 'datahorafimauditoriasistema',
        label: t('s.operacao.recebimento.datahorafimdescarga'),
        format: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'finaldatetime',
          defaultValue: format(new Date(), 'yyyy-MM-dd 23:59:59'),
        },
      },
      {
        id: 'cadastroauditoriasistema',
        label: t('s.relatorio.auditoria.cadastroauditoriasistema'),
        filter: { type: 'text' },
      },
      {
        id: 'chaveauditoriasistema',
        label: t('s.relatorio.auditoria.chaveauditoriasistema'),
        format: (value) => t(value),
        filter: { type: 'text' },
      },
      {
        id: 'codigousuario',
        label: t('s.relatorio.reltransferencia.codigousuario'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeusuario',
        label: t('s.geral.camposistema.nomeusuario'),
        filter: { type: 'text' },
      },
      {
        id: 'acaoauditoriasistema',
        label: t('s.relatorio.auditoria.acaoauditoriasistema'),
        converter: optionsActions,
        filter: { type: 'select', options: optionsActions },
      },
    ],
    [optionsActions]
  );

  const buttons = useMemo(
    () => [
      {
        label: t('s.geral.botaofechar'),
        callback: () => modalRef.current.handleCloseModal(),
        color: 'secondary',
        autoFocus: true,
        variant: 'outlined',
      },
    ],
    []
  );

  const handleOpenModalWithItemDetail = useCallback(
    async ({ idauditoriasistema }) => {
      try {
        setLoading(true);
        const { data } = await api.get(
          `/auditoriasistemas/${idauditoriasistema}`
        );
        await modalRef.current.handleOpenModal({ data });
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const actions = useMemo(
    () => [
      {
        title: t('s.geral.visualizar'),
        action: (value) => handleOpenModalWithItemDetail(value),
        icon: <Visibility />,
      },
    ],
    []
  );

  return (
    <>
      <Tabela
        read={dataInRequest}
        state={redux}
        header={header}
        buttons={actions}
        recordFilter={recordFiltering}
        headerMobile="auditoriaSistemas"
        unique={['idauditoriasistema']}
      />

      <Modal
        disableBackdropClick
        disableEscapeKeyDown
        ref={modalRef}
        loading={loading}
        component={ItemDetail}
        actions={buttons}
        title={t('s.menu.relatorio.auditoria')}
      />
    </>
  );
}
